import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Modal,
  Stack,
  TextField
} from '@mui/material';
import CloseModalIcon from 'src/content/common/Common';
import { Cotisation, CotisationFormData, UserInfos } from 'src/types';
import { useCreateCotisation } from 'src/utils/api/hooks';

import { useEffect, useMemo, useState } from 'react';
import { modalStyles } from 'src/utils/constants';
import { useFormData } from 'src/utils/form';
import { useAppSelector } from '../../../redux/root';
import { Select } from '../../common/Select';

type CotisationModalProps = {
  show: boolean;
  onHide: () => any;
  item?: Cotisation;
  type: Cotisation['type'];
};

const DepValidate = (yup: any) => {
  return {
    titre: yup.string().required().label('Description'),
    amount: yup.number().required().label('Montant')
  };
};

const CostisationValidate = (yup: any) => {
  return {
    name: yup.string().required().label('Prénom & Nom'),
    amount: yup.number().required().label('Montant'),
    phone: yup
      .string()
      .required()
      .label('Téléphone')
      .test('validate-phone', 'Numéro téléphone non valid', function (value) {
        if (value) {
          return /^(\+)?\d+(\s?\d+)*$/.test(value);
        }
        return true;
      })
  };
};

export function CotisationModal({
  show,
  onHide,
  item,
  type
}: CotisationModalProps) {
  const depType = item?.type || type;
  if (depType === 'depense')
    return <DepenseModal {...{ item, show, onHide, type }} />;

  return <CotisationFormModal {...{ item, show, onHide, type }} />;
}

function CotisationFormModal({
  show,
  onHide,
  item,
  type
}: CotisationModalProps) {
  const { loading, onAddKhassida } = useCreateCotisation(() => {
    onHide();
  }, item);

  const [value, setValue] = useState<UserInfos>();
  const allUsers = useAppSelector((s) => s.admins);
  const users = useMemo(() => {
    return allUsers.filter((u) => !u.deleted && !u.disabled);
  }, [allUsers.length]);

  useEffect(() => {
    if (item) {
      const finded = users.find((u) => u.uid === item.user);
      setValue(finded);
    }
  }, [users.length]);

  const { register, onSubmitForm, data, onChange } =
    useFormData<CotisationFormData>(
      {
        name: item?.name ?? '',
        amount: item?.amount ?? '',
        phone: item?.phone ?? '',
        titre: item?.titre ?? '',
        user: item?.user ?? '',
        type: item?.type ?? type ?? 'cotisation'
      },
      (yup) => CostisationValidate(yup)
    );

  const getLabel = (u: UserInfos) =>
    `${u.firstName} ${u.lastName} ${u.phone} ${
      u.matricule ?? u.uid.slice(0, 4)
    }`;

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon
          title={`${item ? 'Modifier' : 'Ajouter'} une cotisation`}
          {...{ onHide }}
        />
        <form
          onSubmit={onSubmitForm((data) => {
            onAddKhassida(data);
          })}
        >
          <Autocomplete
            id="combo-box-demo"
            value={value}
            clearOnBlur
            fullWidth
            onChange={(event: any, newValue: UserInfos | null) => {
              if (newValue) {
                setValue(newValue);
                onChange({
                  name: `${newValue.firstName} ${newValue.lastName}`,
                  phone: newValue.phone,
                  user: newValue.uid
                });
              }
            }}
            filterOptions={(users, state) => {
              return users.filter((u) =>
                getLabel(u)
                  .toLowerCase()
                  .includes(state.inputValue.toLowerCase())
              );
            }}
            getOptionLabel={getLabel}
            options={users}
            renderInput={(params) => (
              <TextField {...params} label="Utilisateur" />
            )}
          />
          {!data.user && (
            <>
              <TextField
                type="text"
                {...register('name')}
                margin="normal"
                required
                fullWidth
                placeholder={'Prénom & Nom'}
                label={'Prénom & Nom'}
                variant="outlined"
              />
              <TextField
                type="tel"
                {...register('phone')}
                margin="normal"
                required
                fullWidth
                placeholder={'Téléphone'}
                label={'Téléphone'}
                variant="outlined"
              />
            </>
          )}
          <Select
            {...register('type')}
            margin="normal"
            required
            fullWidth
            placeholder={'Type'}
            label={'Type'}
            variant="outlined"
            data={[
              { label: 'Cotisation', value: 'cotisation' },
              { label: 'Inscription', value: 'inscription' },
              { label: 'Barkélou', value: 'autre' }
            ]}
          />
          <TextField
            type="number"
            {...register('amount')}
            margin="normal"
            required
            fullWidth
            placeholder={'Montant'}
            label={'Montant'}
            variant="outlined"
          />

          <Stack mt={3} direction="row" spacing={2}>
            <Button
              fullWidth
              onClick={onHide}
              variant="contained"
              color="error"
            >
              Annuler
            </Button>
            <LoadingButton
              loading={loading}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              {item ? 'Modifier' : 'Ajouter'}
            </LoadingButton>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}

function DepenseModal({ show, onHide, item, type }: CotisationModalProps) {
  const { loading, onAddKhassida } = useCreateCotisation(() => {
    onHide();
  }, item);
  const { register, onSubmitForm } = useFormData<CotisationFormData>(
    {
      name: item?.name ?? '',
      amount: item?.amount ?? '',
      titre: item?.titre ?? '',
      type: 'depense'
    },
    (yup) => DepValidate(yup)
  );

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon
          title={`${item ? 'Modifier' : 'Ajouter'} une dépense`}
          {...{ onHide }}
        />
        <form
          onSubmit={onSubmitForm((data) => {
            onAddKhassida(data);
          })}
        >
          <TextField
            type="text"
            {...register('titre')}
            margin="normal"
            required
            fullWidth
            placeholder={'Description'}
            label={'Description'}
            variant="outlined"
          />

          <TextField
            type="number"
            {...register('amount')}
            margin="normal"
            required
            fullWidth
            placeholder={'Montant'}
            label={'Montant'}
            variant="outlined"
          />
          <Stack mt={3} direction="row" spacing={2}>
            <Button
              fullWidth
              onClick={onHide}
              variant="contained"
              color="error"
            >
              Annuler
            </Button>
            <LoadingButton
              loading={loading}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              {item ? 'Modifier' : 'Ajouter'}
            </LoadingButton>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}

export function AutoCotisationModal({
  show,
  onHide,
  item,
  defaultType
}: {
  show: boolean;
  onHide: () => void;
  item: UserInfos;
  defaultType: Cotisation['type'];
}) {
  const { loading, onAddKhassida } = useCreateCotisation(() => {
    onHide();
  });
  const { register, onSubmitForm, data, onChange } =
    useFormData<CotisationFormData>(
      {
        name: `${item.firstName} ${item.lastName}`,
        phone: item.phone,
        user: item.uid,
        amount: defaultType === 'autre' ? 3000 : 500,
        type: defaultType
      },
      (yup) => CostisationValidate(yup)
    );

  useEffect(() => {
    if (data?.type === 'autre') {
      onChange('amount', 3000);
    } else {
      onChange('amount', 500);
    }
  }, [data?.type]);

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles}>
        <CloseModalIcon
          title={`Cotisation ${item.firstName}`}
          {...{ onHide }}
        />
        <form
          onSubmit={onSubmitForm((data) => {
            onAddKhassida(data);
          })}
        >
          <Select
            {...register('type')}
            margin="normal"
            required
            fullWidth
            placeholder={'Type'}
            label={'Type'}
            variant="outlined"
            data={[
              { label: 'Cotisation', value: 'cotisation' },
              { label: 'Barkélou', value: 'autre' }
            ]}
          />
          <TextField
            type="number"
            {...register('amount')}
            margin="normal"
            required
            fullWidth
            placeholder={'Montant'}
            label={'Montant'}
            variant="outlined"
          />
          <Stack mt={3} direction="row" spacing={2}>
            <Button
              fullWidth
              onClick={onHide}
              variant="contained"
              color="error"
            >
              Annuler
            </Button>
            <LoadingButton
              loading={loading}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              {'Valider'}
            </LoadingButton>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}

export function UpdateCotisationModal(
  props: CotisationModalProps & { item: Cotisation }
) {
  if (!props.show) return null;
  return <CotisationModal {...props} />;
}
